import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ScrollToTop from "../ScrollToTop";
import AdminSidebar from "../../Component/AdminSidebar";
import { deleteCustomer, loadCustomers } from "../../data/customers";
import { loadSAVThreads, updateSAVStatus } from "../../data/sav";

const initialFilters = {
  id: "",
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  type: "",
  active: "",
  newsletter: "",
  optin: "",
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

const BoolIcon = ({ value, positive, negative }) =>
  value ? (
    <span className="ck-status-icon text-success" title={positive}><i className="fas fa-check-circle" /></span>
  ) : (
    <span className="ck-status-icon text-muted" title={negative}><i className="fas fa-times-circle" /></span>
  );

const typeBadge = (type) => {
  switch (type) {
    case "visiteur": return "bg-secondary";
    case "boutique": return "bg-info text-dark";
    case "restaurant": return "bg-warning text-dark";
    case "pro": return "bg-dark";
    default: return "bg-primary";
  }
};

const savStatusBadge = (status) => {
  switch (status) {
    case "Ouvert": return "bg-primary";
    case "En attente 1": return "bg-warning text-dark";
    case "En attente 2": return "bg-danger";
    case "Fermé": return "bg-secondary";
    default: return "bg-secondary";
  }
};

const savStatusDot = (status) => {
  switch (status) {
    case "Ouvert": return "#0d6efd";
    case "En attente 1": return "#ffc107";
    case "En attente 2": return "#dc3545";
    case "Fermé": return "#6c757d";
    default: return "#6c757d";
  }
};

const formatSAVDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

const RowActions = ({ customer, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="ck-dropdown" ref={ref}>
      <div className="btn-group btn-group-sm">
        <Link to={`/MyAccountCustomerForm/${customer.id}`} className="btn btn-outline-primary ck-dropdown-btn">Modifier</Link>
        <button
          type="button"
          className="btn btn-outline-primary ck-dropdown-caret"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label="Plus d'actions"
        >
          <i className={`fas fa-chevron-${open ? "up" : "down"}`} />
        </button>
      </div>
      {open && (
        <div className="ck-dropdown-menu">
          <Link to={`/MyAccountCustomerForm/${customer.id}`} className="ck-dropdown-item">
            <i className="fas fa-pen text-primary" /> Modifier
          </Link>
          <button type="button" className="ck-dropdown-item text-danger" onClick={() => { setOpen(false); onDelete(customer.id); }}>
            <i className="fas fa-trash" /> Supprimer
          </button>
        </div>
      )}
    </div>
  );
};

const MyAccountCustomers = () => {
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [message, setMessage] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTab, setActiveTab] = useState("list");
  const [savThreads, setSavThreads] = useState([]);
  const [savFilters, setSavFilters] = useState({ id: "", email: "", status: "", employee: "" });
  const [selectedSAVIds, setSelectedSAVIds] = useState([]);

  useEffect(() => {
    let isMounted = true;
    loadCustomers().then((loaded) => {
      if (!isMounted) return;
      setCustomers(loaded);
      setLoaderStatus(false);
    });
    loadSAVThreads().then((threads) => {
      if (!isMounted) return;
      setSavThreads(threads);
    });
    return () => { isMounted = false; };
  }, []);

  const filtered = useMemo(() => {
    const m = (v) => (v || "").toString().toLowerCase();
    return customers.filter((c) => {
      if (filters.id && !m(c.id).includes(m(filters.id))) return false;
      if (filters.firstName && !m(c.firstName).includes(m(filters.firstName))) return false;
      if (filters.lastName && !m(c.lastName).includes(m(filters.lastName))) return false;
      if (filters.email && !m(c.email).includes(m(filters.email))) return false;
      if (filters.company && !m(c.company).includes(m(filters.company))) return false;
      if (filters.type && c.type !== filters.type) return false;
      if (filters.active !== "" && String(Boolean(c.active)) !== filters.active) return false;
      if (filters.newsletter !== "" && String(Boolean(c.newsletter)) !== filters.newsletter) return false;
      if (filters.optin !== "" && String(Boolean(c.newsletterOptin)) !== filters.optin) return false;
      return true;
    });
  }, [customers, filters]);

  const totalSpent = useMemo(() => customers.reduce((sum, c) => sum + Number(c.totalSpent || 0), 0), [customers]);
  const totalOrders = useMemo(() => customers.reduce((sum, c) => sum + Number(c.totalOrders || 0), 0), [customers]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selectedIds.includes(c.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filtered.some((c) => c.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...filtered.map((c) => c.id)])));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce client ?")) return;
    await deleteCustomer(id);
    setCustomers((prev) => prev.filter((c) => c.id !== id));
    setSelectedIds((prev) => prev.filter((x) => x !== id));
    setMessage("Client supprime.");
  };

  const setFilter = (key) => (e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const filteredSAV = useMemo(() => {
    const m = (v) => (v || "").toString().toLowerCase();
    return savThreads.filter((t) => {
      if (savFilters.id && !m(t.id).includes(m(savFilters.id))) return false;
      if (savFilters.email && !m(t.clientEmail).includes(m(savFilters.email))) return false;
      if (savFilters.status && t.status !== savFilters.status) return false;
      if (savFilters.employee && !m(t.employee).includes(m(savFilters.employee))) return false;
      return true;
    });
  }, [savThreads, savFilters]);

  const savPendingCount = useMemo(
    () => savThreads.filter((t) => t.status === "En attente 1" || t.status === "En attente 2").length,
    [savThreads]
  );
  const savOpenCount = useMemo(() => savThreads.filter((t) => t.status === "Ouvert").length, [savThreads]);
  const savClosedCount = useMemo(() => savThreads.filter((t) => t.status === "Fermé").length, [savThreads]);

  const allFilteredSAVSelected = filteredSAV.length > 0 && filteredSAV.every((t) => selectedSAVIds.includes(t.id));

  const toggleSelectAllSAV = () => {
    if (allFilteredSAVSelected) {
      setSelectedSAVIds((prev) => prev.filter((id) => !filteredSAV.some((t) => t.id === id)));
    } else {
      setSelectedSAVIds((prev) => Array.from(new Set([...prev, ...filteredSAV.map((t) => t.id)])));
    }
  };

  const toggleSelectSAV = (id) => {
    setSelectedSAVIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const setSAVFilter = (key) => (e) => setSavFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSAVStatusBulk = async (status) => {
    for (const id of selectedSAVIds) {
      await updateSAVStatus(id, status);
    }
    setSavThreads(await loadSAVThreads());
    setSelectedSAVIds([]);
  };

  if (loaderStatus) {
    return (
      <div>
        <ScrollToTop />
        <section className="admin-section">
          <div className="container">
            <div className="row">
              <AdminSidebar activePath="/MyAccountCustomers" />
              <div className="col-lg-9 col-md-8 col-12">
                <div className="p-6 p-lg-10">
                  <div className="alert alert-light border mb-0">Chargement des clients...</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <ScrollToTop />
      <section className="admin-section">
        <div className="container">
          <div className="row">
            <AdminSidebar activePath="/MyAccountCustomers" />
            <div className="col-lg-9 col-md-8 col-12">
              <div className="p-6 p-lg-10">
                <div className="d-flex justify-content-between align-items-center mb-6 flex-wrap gap-2">
                  <div>
                    <h2 className="mb-1">Gerez vos clients</h2>
                    <p className="text-muted mb-0">Base de donnees clients, historique d'achats et informations de contact.</p>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <Link to="/MyAccountCustomerForm" className="btn btn-primary"><i className="fas fa-plus me-1" />Ajouter un nouveau client</Link>
                    <Link to="/MyAccountCashier" className="btn btn-outline-primary">Aller a la caisse</Link>
                  </div>
                </div>

                <div className="admin-tabs">
                  {[
                    { key: "list", label: "Liste des clients" },
                    { key: "sav", label: "SAV" },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`admin-tab-btn${activeTab === tab.key ? " active" : ""}`}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {activeTab === "sav" && (
                  <div className="mb-8">
                    <div className="row g-3 mb-8">
                      <div className="col-md-3">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-3 d-flex align-items-center gap-3">
                            <div className="ck-kpi-icon" style={{ background: "#fff3cd", color: "#fd7e14" }}><i className="fas fa-clock" /></div>
                            <div><p className="text-muted mb-1 small">Conversations en attente</p><h4 className="mb-0">{savPendingCount}</h4></div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-3 d-flex align-items-center gap-3">
                            <div className="ck-kpi-icon" style={{ background: "#e7f1ff", color: "#0d6efd" }}><i className="fas fa-stopwatch" /></div>
                            <div><p className="text-muted mb-1 small">Temps de réponse moyen</p><h4 className="mb-0">—</h4></div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-3 d-flex align-items-center gap-3">
                            <div className="ck-kpi-icon" style={{ background: "#d1e7dd", color: "#198754" }}><i className="fas fa-comment-dots" /></div>
                            <div><p className="text-muted mb-1 small">Messages par fil</p><h4 className="mb-0">{savThreads.length ? (savThreads.reduce((s, t) => s + (t.messages || []).length, 0) / savThreads.length).toFixed(1) : "0"}</h4></div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-3 d-flex align-items-center gap-3">
                            <div className="ck-kpi-icon" style={{ background: "#ede9fe", color: "#6f42c1" }}><i className="fas fa-life-ring" /></div>
                            <div><p className="text-muted mb-1 small">Ouvertes / Fermées</p><h4 className="mb-0">{savOpenCount} / {savClosedCount}</h4></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <div className="card border-0 shadow-sm h-100 bg-light">
                          <div className="card-body p-3">
                            <strong className="small">Webmaster</strong>
                            <p className="text-muted small mb-0 mt-1">Pour toute question technique concernant le site (connexion, panier, paiement, bug), utilisez ce type de demande.</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="card border-0 shadow-sm h-100 bg-light">
                          <div className="card-body p-3">
                            <strong className="small">Service client</strong>
                            <p className="text-muted small mb-0 mt-1">Pour toute question liée aux commandes, livraisons, retours ou remboursements. Légende des statuts: <span className="badge bg-primary">Ouvert</span> <span className="badge bg-warning text-dark">En attente 1</span> <span className="badge bg-danger">En attente 2</span> <span className="badge bg-secondary">Fermé</span></p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card border-0 shadow-sm">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <h5 className="mb-0">Fil de discussion SAV</h5>
                          <div className="d-flex align-items-center gap-3 flex-wrap">
                            {selectedSAVIds.length > 0 && (
                              <div className="d-flex gap-2 align-items-center">
                                <span className="small text-muted">{selectedSAVIds.length} sélectionné(s)</span>
                                <button type="button" className="btn btn-sm btn-outline-success" onClick={() => handleSAVStatusBulk("Fermé")}>Fermer</button>
                                <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => handleSAVStatusBulk("En attente 1")}>En attente 1</button>
                              </div>
                            )}
                            <span className="small text-muted">{filteredSAV.length} sur {savThreads.length} fil(s)</span>
                          </div>
                        </div>
                        <div className="table-responsive ck-customers-scroll">
                          <table className="table table-sm table-hover align-middle mb-0 ck-customers-table">
                            <thead className="table-light">
                              <tr>
                                <th className="ck-col-check" style={{ width: 36 }}>
                                  <input type="checkbox" className="form-check-input" checked={allFilteredSAVSelected} onChange={toggleSelectAllSAV} aria-label="Tout sélectionner" />
                                </th>
                                <th style={{ width: "6%" }}>ID</th>
                                <th style={{ width: "13%" }}>Client</th>
                                <th style={{ width: "14%" }}>Email</th>
                                <th style={{ width: "10%" }}>Type</th>
                                <th style={{ width: "8%" }}>Langue</th>
                                <th style={{ width: "10%" }}>Statut</th>
                                <th style={{ width: "10%" }}>Employé</th>
                                <th style={{ width: "12%" }}>Dernier message</th>
                                <th style={{ width: "6%" }}>Privé</th>
                                <th style={{ width: "10%" }}>Actions</th>
                              </tr>
                              <tr>
                                <th className="ck-col-check" style={{ width: 36 }} />
                                <th className="ck-filter-cell">
                                  <input type="text" className="form-control form-control-sm" placeholder="ID" value={savFilters.id} onChange={setSAVFilter("id")} aria-label="Filtrer ID" />
                                </th>
                                <th className="ck-filter-cell" />
                                <th className="ck-filter-cell">
                                  <input type="text" className="form-control form-control-sm" placeholder="Email" value={savFilters.email} onChange={setSAVFilter("email")} aria-label="Filtrer email" />
                                </th>
                                <th className="ck-filter-cell" />
                                <th className="ck-filter-cell" />
                                <th className="ck-filter-cell">
                                  <select className="form-select form-select-sm" value={savFilters.status} onChange={setSAVFilter("status")} aria-label="Filtrer statut">
                                    <option value="">Tous</option>
                                    <option value="Ouvert">Ouvert</option>
                                    <option value="En attente 1">En attente 1</option>
                                    <option value="En attente 2">En attente 2</option>
                                    <option value="Fermé">Fermé</option>
                                  </select>
                                </th>
                                <th className="ck-filter-cell">
                                  <input type="text" className="form-control form-control-sm" placeholder="Employé" value={savFilters.employee} onChange={setSAVFilter("employee")} aria-label="Filtrer employé" />
                                </th>
                                <th className="ck-filter-cell" />
                                <th className="ck-filter-cell" />
                                <th className="ck-filter-cell" />
                              </tr>
                            </thead>
                            <tbody>
                              {filteredSAV.map((t) => (
                                <tr
                                  key={t.id}
                                  role="button"
                                  className={selectedSAVIds.includes(t.id) ? "table-primary" : ""}
                                  onClick={() => { window.location.href = `/MyAccountSAVDetail/${t.id}`; }}
                                >
                                  <td className="ck-col-check" style={{ width: 36 }} onClick={(e) => e.stopPropagation()}>
                                    <input type="checkbox" className="form-check-input" checked={selectedSAVIds.includes(t.id)} onChange={() => toggleSelectSAV(t.id)} aria-label={`Sélectionner ${t.clientName}`} />
                                  </td>
                                  <td><span className="badge bg-secondary">{t.id}</span></td>
                                  <td><small className="fw-semibold">{t.clientName}</small></td>
                                  <td><small className="ck-truncate d-block" title={t.clientEmail}>{t.clientEmail}</small></td>
                                  <td><small>{t.type}</small></td>
                                  <td><small>{t.language}</small></td>
                                  <td>
                                    <span className="d-inline-flex align-items-center gap-1">
                                      <span className="sav-status-dot" style={{ background: savStatusDot(t.status), width: 8, height: 8, borderRadius: "50%", display: "inline-block" }} />
                                      <span className={`badge ${savStatusBadge(t.status)}`}>{t.status}</span>
                                    </span>
                                  </td>
                                  <td><small>{t.employee || "—"}</small></td>
                                  <td><small className="ck-nowrap">{formatSAVDateTime(t.lastMessageDate)}</small></td>
                                  <td>{t.isPrivate ? <i className="fas fa-lock text-muted" /> : <i className="fas fa-globe text-muted" />}</td>
                                  <td>
                                    <Link to={`/MyAccountSAVDetail/${t.id}`} className="btn btn-sm btn-outline-primary" onClick={(e) => e.stopPropagation()}>
                                      <i className="fas fa-eye me-1" />Afficher
                                    </Link>
                                  </td>
                                </tr>
                              ))}
                              {!filteredSAV.length && (
                                <tr><td colSpan="11" className="text-center text-muted py-4">Aucun fil de discussion trouvé.</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab !== "sav" && (
                <>
                <div className="row g-4 mb-8">
                  <div className="col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-3">
                        <div className="ck-kpi-icon" style={{ background: "#e7f1ff", color: "#0d6efd" }}><i className="fas fa-users" /></div>
                        <div><p className="text-muted mb-1 small">Clients enregistres</p><h4 className="mb-0">{customers.length}</h4></div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-3">
                        <div className="ck-kpi-icon" style={{ background: "#d1e7dd", color: "#198754" }}><i className="fas fa-receipt" /></div>
                        <div><p className="text-muted mb-1 small">Total commandes</p><h4 className="mb-0">{totalOrders}</h4></div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-3">
                        <div className="ck-kpi-icon" style={{ background: "#ede9fe", color: "#6f42c1" }}><i className="fas fa-euro-sign" /></div>
                        <div><p className="text-muted mb-1 small">Chiffre d'affaires clients</p><h4 className="mb-0">{totalSpent.toFixed(2)} EUR</h4></div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-3">
                        <div className="ck-kpi-icon" style={{ background: "#fff3cd", color: "#fd7e14" }}><i className="fas fa-calculator" /></div>
                        <div><p className="text-muted mb-1 small">Panier moyen</p><h4 className="mb-0">{customers.length ? (totalSpent / customers.length).toFixed(2) : "0.00"} EUR</h4></div>
                      </div>
                    </div>
                  </div>
                </div>

                {message && <div className="alert alert-info py-2">{message}</div>}

                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <h5 className="mb-0">Liste des clients</h5>
                      <div className="d-flex align-items-center gap-2">
                        {selectedIds.length > 0 && <span className="small text-muted">{selectedIds.length} selectionne(s)</span>}
                        <span className="small text-muted">{filtered.length} sur {customers.length} client(s)</span>
                      </div>
                    </div>
                    <div className="table-responsive ck-customers-scroll">
                      <table className="table table-sm table-hover align-middle mb-0 ck-customers-table">
                        <thead className="table-light">
                          <tr>
                            <th className="ck-col-check" style={{ width: 36 }}>
                              <input type="checkbox" className="form-check-input" checked={allFilteredSelected} onChange={toggleSelectAll} aria-label="Tout selectionner" />
                            </th>
                            <th style={{ width: "7%" }}>ID</th>
                            <th style={{ width: "4.5%" }}>Titre</th>
                            <th style={{ width: "8%" }}>Prenom</th>
                            <th style={{ width: "8%" }}>Nom</th>
                            <th style={{ width: "13%" }}>Adresse e-mail</th>
                            <th style={{ width: "8%" }}>Societe</th>
                            <th style={{ width: "8%" }}>Categorie</th>
                            <th style={{ width: "7%" }}>Ventes</th>
                            <th style={{ width: "5%" }}>Actif</th>
                            <th style={{ width: "6%" }}>Newsletter</th>
                            <th style={{ width: "6%" }}>Opt-in</th>
                            <th style={{ width: "8%" }}>Inscription</th>
                            <th style={{ width: "8%" }}>Derniere visite</th>
                            <th style={{ width: "12%" }}>Actions</th>
                          </tr>
                          <tr>
                            <th className="ck-col-check" style={{ width: 36 }} />
                            <th className="ck-filter-cell">
                              <input type="text" className="form-control form-control-sm" placeholder="Ref" value={filters.id} onChange={setFilter("id")} aria-label="Filtrer ID" />
                            </th>
                            <th className="ck-filter-cell" />
                            <th className="ck-filter-cell">
                              <input type="text" className="form-control form-control-sm" placeholder="Prenom" value={filters.firstName} onChange={setFilter("firstName")} aria-label="Filtrer prenom" />
                            </th>
                            <th className="ck-filter-cell">
                              <input type="text" className="form-control form-control-sm" placeholder="Nom" value={filters.lastName} onChange={setFilter("lastName")} aria-label="Filtrer nom" />
                            </th>
                            <th className="ck-filter-cell">
                              <input type="text" className="form-control form-control-sm" placeholder="Email" value={filters.email} onChange={setFilter("email")} aria-label="Filtrer email" />
                            </th>
                            <th className="ck-filter-cell">
                              <input type="text" className="form-control form-control-sm" placeholder="Societe" value={filters.company} onChange={setFilter("company")} aria-label="Filtrer societe" />
                            </th>
                            <th className="ck-filter-cell">
                              <select className="form-select form-select-sm" value={filters.type} onChange={setFilter("type")} aria-label="Filtrer categorie">
                                <option value="">Toutes</option>
                                <option value="client">Client</option>
                                <option value="visiteur">Visiteur</option>
                                <option value="boutique">Boutique</option>
                                <option value="restaurant">Restaurant</option>
                                <option value="pro">Pro</option>
                              </select>
                            </th>
                            <th className="ck-filter-cell" />
                            <th className="ck-filter-cell">
                              <select className="form-select form-select-sm" value={filters.active} onChange={setFilter("active")} aria-label="Filtrer actif">
                                <option value="">Tous</option>
                                <option value="true">Oui</option>
                                <option value="false">Non</option>
                              </select>
                            </th>
                            <th className="ck-filter-cell">
                              <select className="form-select form-select-sm" value={filters.newsletter} onChange={setFilter("newsletter")} aria-label="Filtrer newsletter">
                                <option value="">Tous</option>
                                <option value="true">Oui</option>
                                <option value="false">Non</option>
                              </select>
                            </th>
                            <th className="ck-filter-cell">
                              <select className="form-select form-select-sm" value={filters.optin} onChange={setFilter("optin")} aria-label="Filtrer opt-in">
                                <option value="">Tous</option>
                                <option value="true">Oui</option>
                                <option value="false">Non</option>
                              </select>
                            </th>
                            <th className="ck-filter-cell" />
                            <th className="ck-filter-cell" />
                            <th className="ck-filter-cell" />
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((c) => (
                            <tr key={c.id} className={selectedIds.includes(c.id) ? "table-primary" : ""}>
                              <td className="ck-col-check" style={{ width: 36 }}>
                                <input type="checkbox" className="form-check-input" checked={selectedIds.includes(c.id)} onChange={() => toggleSelect(c.id)} aria-label={`Selectionner ${c.firstName} ${c.lastName}`} />
                              </td>
                              <td className="ck-truncate" title={c.id}><small>{c.id}</small></td>
                              <td><small>{c.title || "—"}</small></td>
                              <td><small>{c.firstName || "—"}</small></td>
                              <td><small>{c.lastName || "—"}</small></td>
                              <td><small className="ck-truncate d-block" title={c.email}>{c.email || "—"}</small></td>
                              <td><small className="ck-truncate d-block" title={c.company}>{c.company || "—"}</small></td>
                              <td><span className={`badge ${typeBadge(c.type)}`}>{c.type ? c.type.charAt(0).toUpperCase() + c.type.slice(1) : "Client"}</span></td>
                              <td><small className="fw-semibold">{Number(c.totalSpent || 0).toFixed(2)} EUR</small></td>
                              <td><BoolIcon value={c.active} positive="Actif" negative="Inactif" /></td>
                              <td><BoolIcon value={c.newsletter} positive="Newsletter" negative="Pas de newsletter" /></td>
                              <td><BoolIcon value={c.newsletterOptin} positive="Opt-in" negative="Pas d'opt-in" /></td>
                              <td><small className="ck-nowrap">{formatDate(c.createdAt)}</small></td>
                              <td><small className="ck-nowrap">{formatDate(c.lastVisit)}</small></td>
                              <td><RowActions customer={c} onDelete={handleDelete} /></td>
                            </tr>
                          ))}
                          {!filtered.length && (
                            <tr>
                              <td colSpan="15" className="text-center text-muted py-4">Aucun client trouve.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyAccountCustomers;